import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { CallHistoryMethodAction, replace } from "connected-react-router";
import i18next from "config/i18next";
import { LoginType } from "pages/login/types";
import { RestorePasswordActionsTypes } from "reducers/types/login";
import { NotificationStateType } from "reducers/types/notification";
import {
  call,
  CallEffect,
  ForkEffect,
  put,
  PutEffect,
  takeLatest,
} from "redux-saga/effects";
import * as loginMiddleware from "../middleware/login";
import { actions } from "../reducers/login";
import { actions as notificationActions } from "../reducers/notification";
import { getValue, removeItem, saveValue } from "../utils/utils";
import { actions as preliquidationActions } from "../reducers/preliquidation";
import { actions as sessionActions } from "../reducers/session";
import {
  EmailType,
  ILoginResponse,
  LoginEmailTypeResponse,
  RestoreEmailResponse,
} from "./types/login";
import { DEFAULT_COUNTRY } from "hooks/useSession";
import { CountryCodeType } from "reducers/types/session";
import { connectionErrorAlert, serverErrorAlert } from "utils/alerts";
import resetData from "utils/resetData";
const sagas: ForkEffect<never>[] = [
  takeLatest(actions.getLoginRequest.type, getLogin),
  takeLatest(actions.logout.type, logout),
  takeLatest(actions.getLoginEmailRequest.type, getLoginEmail),
  takeLatest(actions.getLoginRestoreRequest.type, getLoginRestore),
];

export default sagas;

function* getLogin({
  payload,
}: PayloadAction<LoginType>): Generator<
  | CallEffect<AxiosResponse<ILoginResponse>>
  | PutEffect<{ type: string }>
  | CallEffect<void>,
  void,
  ILoginResponse
> {
  const { countryCode, ...body } = payload;
  const response = yield call(loginMiddleware.getLogin, body, countryCode);
  if (response.status !== 200) {
    yield put(actions.getLoginError());
    switch (response.data.statusCode) {
      case 400:
        yield put(notificationActions.showNotification(connectionErrorAlert()));
        break;
      case 10005:
        yield put(
          notificationActions.showNotification({
            id: "notification-invalid-error",
            level: "error",
            title: i18next.t("login:invalidTitle"),
            body: i18next.t("login:invalidMessage"),
          })
        );
        break;
      case 403:
        yield put(
          notificationActions.showNotification({
            id: "notification-invalid-error",
            level: "error",
            title: i18next.t("login:invalidCountryTitle"),
            body: i18next.t("login:invalidCountryMessage"),
          })
        );
        break;
      default:
        yield put(notificationActions.showNotification(serverErrorAlert()));
        break;
    }
  } else {
    const { result } = response.data;
    const { accessToken } = result;
    yield call(loginMiddleware.setAuthToken, accessToken);
    yield put(sessionActions.setCountryCode(countryCode));
    i18next.changeLanguage(`es_${countryCode}`);
    const tokens = getValue("tokens", true);
    saveValue("tokens", { ...tokens, [countryCode]: accessToken });

    resetData();
    yield put(preliquidationActions.resetPendingApprovalPresettlements());
    yield put(replace(`/${countryCode}/`));
    yield put(actions.getLoginSuccess());
  }
}

function* logout(): Generator<
  | PutEffect<CallHistoryMethodAction<[string, unknown?]>>
  | PutEffect<{ payload: CountryCodeType; type: string }>
  | PutEffect<{ payload: undefined; type: string }>,
  void,
  void
> {
  removeItem("tokens");
  yield put(sessionActions.setCountryCode(DEFAULT_COUNTRY));
  i18next.changeLanguage(`es_${DEFAULT_COUNTRY}`);
  yield put(sessionActions.resetLoadersCount());
  yield put(replace("/login"));
}

function* getLoginEmail({
  payload,
}: PayloadAction<EmailType>): Generator<
  | PutEffect<{ payload: NotificationStateType; type: string }>
  | PutEffect<{ payload: undefined; type: string }>
  | CallEffect<AxiosResponse<LoginEmailTypeResponse>>
  | PutEffect<CallHistoryMethodAction<[string, unknown?]>>,
  void,
  ILoginResponse
> {
  const response = yield call(loginMiddleware.sendRestoreEmail, payload);

  if (response.status !== 200) {
    switch (response.data.statusCode) {
      case 400:
      case 403:
      case 404:
        yield put(
          notificationActions.showNotification({
            id: "notification-restore",
            level: "info",
            title: i18next.t("emailRestore:restoreTitle"),
            body: i18next.t("emailRestore:restoreMessage"),
          })
        );
        break;
      default:
        yield put(notificationActions.showNotification(serverErrorAlert()));
        break;
    }
    yield put(replace("/"));
    yield put(actions.getLoginEmailError());
  } else {
    yield put(
      notificationActions.showNotification({
        id: "notification-restore",
        level: "info",
        title: i18next.t("emailRestore:restoreTitle"),
        body: i18next.t("emailRestore:restoreMessage"),
      })
    );
    yield put(replace("/login"));
    yield put(actions.getLoginEmailSuccess());
  }
}

function* getLoginRestore({
  payload,
}: PayloadAction<RestorePasswordActionsTypes>): Generator<
  | PutEffect<{ payload: undefined; type: string }>
  | PutEffect<{ payload: NotificationStateType; type: string }>
  | CallEffect<AxiosResponse<RestoreEmailResponse>>
  | PutEffect<CallHistoryMethodAction<[string, unknown?]>>,
  void,
  ILoginResponse
> {
  const { onClick, ...body } = payload;
  const response = yield call(loginMiddleware.getLoginRestore, body);

  if (response.status !== 200) {
    switch (response.data.statusCode) {
      case 10003:
        yield put(
          notificationActions.showNotification({
            id: "notification-expired-code",
            level: "error",
            title: i18next.t("restorePassword:expiredCodeTitle"),
            body: i18next.t("restorePassword:expiredCodeMessage"),
            onClose: onClick,
          })
        );
        break;
      default:
        yield put(
          notificationActions.showNotification(serverErrorAlert(onClick))
        );
        break;
    }
    yield put(actions.getLoginREstoreError());
  } else {
    yield put(
      notificationActions.showNotification({
        id: "notification-restore-success",
        level: "success",
        title: i18next.t("restorePassword:successfulRestoreTitle"),
        body: i18next.t("restorePassword:successfulRestoreMessage"),
      })
    );
    yield put(replace("/login"));
    yield put(actions.getLoginRestoreSuccess());
  }
}
